import { Form, Layout, Spin } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import { Mode } from 'app/components/commons/Hotel/Types';
import { Notification } from 'app/components/commons/Notification/Notification';
import { Pictures } from 'app/components/commons/Package/Pictures/Pictures';
import { UploadList } from 'app/components/commons/Uploader/UploadList/UploadList';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { ApiError } from 'app/typings/error';
import type { HotelDiscoverFormValues } from 'app/typings/hotels/Hotel/HotelDiscover';
import type { HotelErrorCode } from 'app/utils/errors/HotelErrors';
import { retrieveHotelErrorMessage } from 'app/utils/errors/HotelErrors';

import './CoverAndDiscover.scss';
import { CoverLabel } from './CoverLabel';
import { Discover } from './Discover/Discover';
import { useGetHotelDiscover } from './hooks/useGetHotelDiscover';
import { useSaveHotelDiscover } from './hooks/useSaveHotelDiscover';

const { Content } = Layout;

type Props = {
  hotelId: number;
  headerMenu: ReactNode;
  headerExtra?: ReactNode[];
};

export const CoverAndDiscover = ({
  headerMenu,
  headerExtra,
  hotelId,
}: Props) => {
  const [mode, setMode] = useState<Mode>(Mode.View);
  const [isDirtyMove, setIsDirtyMove] = useState(false);
  const [form] = Form.useForm();
  const { data: hotel } = useGetHotel(hotelId);
  const { data: discover, isLoading } = useGetHotelDiscover({
    hotelId,
    isLegacy: hotel?.isLegacy ?? false,
  });

  const { mutateAsync: saveDiscover, error: editDiscoverError } =
    useSaveHotelDiscover({
      hotelId,
      isLegacy: hotel?.isLegacy ?? false,
    });

  const isInstanceOfApiError = editDiscoverError instanceof ApiError;
  const hotelError =
    isInstanceOfApiError && editDiscoverError.jsonObject
      ? retrieveHotelErrorMessage(
          editDiscoverError.jsonObject.errorCode as HotelErrorCode
        )
      : { errorTitle: '', errorMessage: '' };

  const onFinish = useCallback(
    async (values: HotelDiscoverFormValues) => {
      if (!hotel || !discover) {
        return;
      }

      await saveDiscover({
        coverLabel1: values.coverLabel1,
        coverLabel2: values.coverLabel2,
        horizontalCoverPictureId: values.horizontalCoverPicture[0]?.id,
        verticalCoverPictureId: values.verticalCoverPicture[0]?.id,
        introPictureIds: values.introPictures.map((pic) => pic.id),
        roomPictureIds: values.roomPictures.map((pic) => pic.id),
        experiences: values.experiences.map((xp) => ({
          id: xp.id,
          discover: xp.discover,
          discoverPriority: xp.discoverPriority,
        })),
      });

      setMode(Mode.View);
    },
    [hotel, discover, saveDiscover]
  );

  const onCancel = useCallback(() => {
    setMode(Mode.View);

    form.resetFields();
  }, [form]);

  if (!hotel || isLoading || !discover) {
    return (
      <SDetailLayout
        title={hotel?.name || 'Loading...'}
        backRoute="/hotels"
        mode={Mode.View}
        headerMenu={headerMenu}
        headerExtra={headerExtra}
        isDirty={false}
      >
        <Content className="hotel-detail__content cover-and-discover-loading">
          <Spin size="large" />
        </Content>
      </SDetailLayout>
    );
  }

  const formattedValues: HotelDiscoverFormValues = {
    coverLabel1: discover.coverLabel1,
    coverLabel2: discover.coverLabel2,
    horizontalCoverPicture: discover.horizontalCoverPicture
      ? [discover.horizontalCoverPicture]
      : [],
    verticalCoverPicture: discover.verticalCoverPicture
      ? [discover.verticalCoverPicture]
      : [],
    introPictures: discover.introPictures,
    roomPictures: discover.roomPictures,
    experiences: [...discover.experiences].sort(
      (a, b) => a.discoverPriority - b.discoverPriority
    ),
  };

  return (
    <>
      <FormLegacyRenderProp
        form={form}
        onFinish={onFinish}
        initialValues={formattedValues}
        className="cover-form"
      >
        {(values, { resetFields, isFieldsTouched, submit, setFieldValue }) => (
          <SDetailLayout
            title={hotel.name}
            backRoute="/hotels"
            mode={mode}
            onEdit={() => setMode(Mode.Edit)}
            onSave={() => {
              submit();
              setIsDirtyMove(false);
            }}
            onCancel={() => {
              onCancel();
              setIsDirtyMove(false);
            }}
            reset={resetFields}
            isDirty={isDirtyMove || isFieldsTouched()}
            headerMenu={headerMenu}
            headerExtra={headerExtra}
            isValid={form
              .getFieldsError()
              .every((item) => item.errors.length > 0)}
          >
            <Content className="hotel-detail__content">
              <SCard
                title="Cover title"
                subtitle="Text displayed over the cover images"
                className="scard-with-pictures"
              >
                <CoverLabel mode={mode} values={values} />
              </SCard>
              <SCard
                title="Cover picture"
                className="scard-cover-with-pictures"
              >
                <div className="staycation-card__container">
                  <div>
                    <div className="staycation-card__title">
                      Horizontal cover
                    </div>
                    <div className="staycation-card__subtitle">
                      Picture used to display the cover in most cases, notably
                      the hotel cards and the cover on the hotel page on desktop
                    </div>
                  </div>
                  <Form.Item name="horizontalCoverPicture">
                    {mode === Mode.Edit ? (
                      <Pictures
                        sectionTitle="Cover"
                        hotelId={hotelId}
                        picturesCategories={[{ name: 'Cover', type: 'cover' }]}
                        kind="cover"
                        editable
                        removable
                        modalSubtitle="Select photos or upload new ones to illustrate the cover"
                        maxPictures={1}
                      />
                    ) : (
                      <UploadList
                        pictures={values.horizontalCoverPicture || []}
                        uploadingItems={{}}
                        previewable
                      />
                    )}
                  </Form.Item>
                </div>

                <div className="staycation-card__container">
                  <div>
                    <div className="staycation-card__title">Vertical cover</div>
                    <div className="staycation-card__subtitle">
                      Picture used to display the cover in some cases, notably
                      for daytime package and the cover on the hotel page on
                      mobile
                    </div>
                  </div>

                  <Form.Item name="verticalCoverPicture">
                    {mode === Mode.Edit ? (
                      <Pictures
                        sectionTitle="Cover"
                        hotelId={hotelId}
                        picturesCategories={[{ name: 'Cover', type: 'cover' }]}
                        kind="cover"
                        editable
                        removable
                        modalSubtitle="Select photos or upload new ones to illustrate the cover"
                        maxPictures={1}
                      />
                    ) : (
                      <UploadList
                        pictures={values.verticalCoverPicture || []}
                        uploadingItems={{}}
                        previewable
                      />
                    )}
                  </Form.Item>
                </div>
              </SCard>
              <SCard
                title="Discover"
                subtitle="Gallery that gathers pictures of all the packages included in this hotel"
                className="scard-cover-with-pictures"
              >
                <Discover
                  hotelId={hotelId}
                  values={values}
                  mode={mode}
                  setIsDirtyMove={setIsDirtyMove}
                  form={form}
                  isLegacy={hotel.isLegacy}
                />
              </SCard>
            </Content>
          </SDetailLayout>
        )}
      </FormLegacyRenderProp>
      <Notification
        title={hotelError.errorTitle}
        content={hotelError.errorMessage}
        visible={isInstanceOfApiError}
        type="error"
        hasCancelButton={false}
        centered
      />
    </>
  );
};
